'use client'

import {
  AnimatePresence,
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from 'framer-motion'
import type React from 'react'
import { useState } from 'react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { cn } from '@/lib/cn'
import { getInitials } from '@/utils/getInitials'

type AnimatedTooltipProps = {
  items: {
    id: string
    name: string
    designation: string
    image: string
    status?: 'ACTIVE' | 'INACTIVE'
  }[]
  bottom?: boolean
}

const AnimatedTooltip = ({ items, bottom = false }: AnimatedTooltipProps) => {
  const [hoveredIndex, setHoveredIndex] = useState<string | null>(null)
  const springConfig = { stiffness: 100, damping: 5 }
  const x = useMotionValue(0)
  const rotate = useSpring(
    useTransform(x, [-100, 100], [-45, 45]),
    springConfig,
  )

  const translateX = useSpring(
    useTransform(x, [-100, 100], [-50, 50]),
    springConfig,
  )

  const handleMouseMove = (event: React.MouseEvent<HTMLImageElement>) => {
    const halfWidth = event.currentTarget.offsetWidth / 2
    x.set(event.nativeEvent.offsetX - halfWidth)
  }

  return (
    <>
      {items.map((item, index) => {
        const isInactive = item.status === 'INACTIVE'
        return (
          <div
            className={cn(
              'group relative',
              index === items.length - 1 ? 'mr-0' : '-mr-4',
            )}
            key={item.name}
            onMouseEnter={() => setHoveredIndex(item.id)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <AnimatePresence mode="popLayout">
              {hoveredIndex === item.id && (
                <motion.div
                  initial={{ opacity: 0, y: bottom ? -20 : 20, scale: 0.6 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    transition: {
                      type: 'spring',
                      stiffness: 260,
                      damping: 10,
                    },
                  }}
                  exit={{ opacity: 0, y: bottom ? -20 : 20, scale: 0.6 }}
                  style={{
                    translateX,
                    rotate,
                    whiteSpace: 'nowrap',
                  }}
                  className={cn(
                    'absolute z-50 flex translate-x-1/2 flex-col items-center justify-center rounded-md bg-black px-4 py-2 text-xs shadow-xl',
                    bottom ? '-bottom-16 -left-1/2' : '-left-1/2 -top-16',
                  )}
                >
                  <div
                    className={cn(
                      'absolute z-30 h-px w-1/5 bg-gradient-to-r from-transparent via-system-primary-purple to-transparent',
                      bottom ? 'inset-x-10 -top-px' : 'inset-x-10 -bottom-px',
                    )}
                  />
                  <div
                    className={cn(
                      'absolute z-30 h-px w-2/5 bg-gradient-to-r from-transparent via-system-primary-avantti to-transparent',
                      bottom ? '-top-px left-10' : '-bottom-px left-10',
                    )}
                  />
                  <div className="relative z-30 text-base font-bold text-white">
                    {item.name}
                  </div>
                  <div className="text-xs text-white">{item.designation}</div>
                  {isInactive && (
                    <div className="text-xs text-red-400">Inativo</div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
            <Avatar
              className={cn(
                'relative !m-0 size-10 rounded-full border-2 border-white object-cover object-top !p-0 transition duration-500 group-hover:z-30 group-hover:scale-105',
                isInactive && 'grayscale opacity-60',
              )}
              onMouseMove={handleMouseMove}
            >
              <AvatarImage
                className="size-10"
                height={100}
                width={100}
                src={item.image}
                alt={item.name}
              />
              <AvatarFallback>{getInitials(item.name)}</AvatarFallback>
            </Avatar>
          </div>
        )
      })}
    </>
  )
}
AnimatedTooltip.displayName = 'AnimatedTooltip'

export { AnimatedTooltip }
